import { FC, useEffect, useMemo, useState } from "react";
import Modal from "../../components/Modal";
import ShipmentResume from "./ShipmentResume";
import {
  CouponRedeemedAnimation,
  CouponRedeemedValue,
} from "../../components/Shipment/CouponRedeemedAnimation";
import { isVuelveTuEnvioPremiadoItem } from "../../components/Shipment/CouponLotteryBadge";
import ShipmentFormList from "./ShipmentFormList";
import PaymentList from "../../components/Payment/PaymentList";
import PaymentTotal from "../../components/Payment/PaymentTotal";
import HorizontalPadding from "../../components/HorizontalPadding";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import ShipmentOwner from "../../components/Shipment/ShipmentOwner";
import { currencyExchange, useCurrencyExchanges } from "../../utils";
import {
  hasFiscalAddress,
  missingFiscalAddressCopy,
} from "../../utils/billingValidations";
import ShipmentCreateClientModal from "../../components/Account/CreateClientModal";
import { searchLiabilityWaiver } from "../../services/liabilityWaiverServices";
import { UserInterface } from "../../interfaces/UserInterface";
import classNames from "classnames";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import WarningModal from "../../components/WarningModal";
import { merchantLogService } from "../../services/logServices";
import { ShipmentBarcodeDTO } from "../../interfaces/Dtos/ShipmentBarcodeDTO";
import { r4InstantCredit, r4InstantDebit } from "../../services/r4BankServices";
import ShipmentTable, {
  ShipmentField,
} from "../../components/Shipment/ShipmentTable";
import {
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/20/solid";
import {
  consultBC,
  sendC2PBC,
  sendB2PBC,
  payMerchant,
  saveDocument,
  saveShipment,
  alertService,
  loaderService,
  saveProofOfPayment,
  savePayment,
  updateDocumentStatus,
  getDocument,
  updatePayment,
  annulationMerchant,
  mercantilChange,
  mercantilC2P,
  mercantilMobilePaymentSearch,
  mercantilTransferSearch,
  updateShipment,
  updateShipmentStatus,
  updateDocumentAccountBillTo,
  ReintegrationRequest,
  deleteFileDO,
  getShipmentBarcodes,
  getShipmentRates,
} from "../../services";
import {
  BackButton,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons";
import {
  ShipmentCreatePhase,
  clearShipmentCreate,
  setShipmentCreateList,
  setShipmentCreateOwner,
  setShipmentCreatePhase,
  setShipmentCreateTaxes,
  setShipmentCreateDocument,
  setShipmentCreateDrafts,
} from "../../store/slices";
import {
  PaymentMode,
  TaxInterface,
  PaymentInterface,
  DocumentInterface,
  ShipmentInterface,
  ShipmentStatus,
  DocumentStatus,
  Undefinable,
  PaymentMethodEnum,
  DocumentType,
  PaymentStatusEnum,
  ShipmentService,
  DeliveryType,
  AccountInterface,
  BankAccountInterface,
  BankEnum,
  PaymentDetailsDto,
} from "../../interfaces";
import { MegaphoneIcon } from "@heroicons/react/24/outline";

const ModalMegaphoneIcon = MegaphoneIcon;

const ShipmentCreate: FC<{}> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const exchanges = useCurrencyExchanges();
  const user = useAppSelector((state) => state.user.user);
  const userBU = useAppSelector((state) => state.user.businessUnit);
  const VTIDs = useAppSelector((state) => state.inmutable.vtid);
  const applicationID = useAppSelector(
    (state) => state.inmutable.appData.applicationID,
  );

  const loading = loaderService.useIsLoading();
  const [redeemedCouponValue, setRedeemedCouponValue] =
    useState<CouponRedeemedValue>(null);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [openCreationModal, setOpenCreationModal] = useState(false);
  const [liabilityWaiverExist, setLiabilityWaiverExist] = useState(false);
  const [openPOPWarningModal, setOpenPOPWarningModal] = useState(false);
  const [openShipmentWarningModal, setOpenShipmentWarningModal] =
    useState(false);
  const coupon = useAppSelector((state) => state.shipmentCreate.coupon);
  const taxes = useAppSelector((state) => state.shipmentCreate.taxes);
  const owner = useAppSelector((state) => state.shipmentCreate.owner);
  const phase = useAppSelector((state) => state.shipmentCreate.phase);
  const businessUnit = useAppSelector((state) => state.user.businessUnit);
  const document = useAppSelector((state) => state.shipmentCreate.document);
  const payments = useAppSelector((state) => state.shipmentCreate.payments);
  const shipments = useAppSelector((state) => state.shipmentCreate.shipments);
  const drafts = useAppSelector((state) => state.shipmentCreate.shipmentDrafts);
  const [shipmentBarcodes, setShipmentBarcodes] = useState<
    ShipmentBarcodeDTO[]
  >([]);
  const bsExchange = useAppSelector(
    (state) => state.inmutable.exchangeRate,
  ).exchangeRate;

  const [invoices, setInvoices] = useState<File[][]>([[]]);

  const items = useMemo(() => {
    return shipments
      .map((shipment) => shipment?.items)
      .reduce((acc, curr) => [...acc, ...curr], []);
  }, [shipments]);

  const subTotal = useMemo(() => {
    return +shipments
      .reduce((acc, shipment) => {
        return (
          acc +
          (shipment?.items.reduce((acc, item) => acc + item.rate.value, 0) ?? 0)
        );
      }, 0)
      .toFixed(2);
  }, [shipments]);

  const ipostel = useMemo(() => {
    return (
      document?.ipostel ??
      document?.taxes.find((t) => t.name === "Ipostel")?.value ??
      shipments.reduce((acc, shipment) => {
        return (
          acc + shipment.items.reduce((acc, item) => acc + item.rate.ipostel, 0)
        );
      }, 0)
    );
  }, [shipments, document?.ipostel, document?.taxes]);

  const iva = useMemo(() => {
    return shipments.reduce((acc, shipment) => {
      return acc + shipment.items.reduce((acc, item) => acc + item.rate.iva, 0);
    }, 0);
  }, [shipments]);

  const total = useMemo(() => {
    return +(
      subTotal +
      taxes?.reduce((acc, tax) => {
        if (tax.type === "percentage") {
          return acc + subTotal * (tax.value / 100);
        }
        return acc + tax.value;
      }, 0)
    ).toFixed(2);
  }, [subTotal, taxes]);

  const remaining = useMemo(() => {
    const result =
      document?.balanceAmount !== undefined
        ? document?.balanceAmount -
          payments
            .filter((p) => p.status === PaymentStatusEnum.PENDING)
            .reduce((acc, payment) => acc + payment.amount, 0)
        : +(
            total -
            payments
              .filter(
                (p) =>
                  p.status !== PaymentStatusEnum.CANCELED &&
                  p.status !== PaymentStatusEnum.REJECT,
              )
              .reduce((acc, payment) => acc + payment.amount, 0)
          ).toFixed(2);
    return result;
  }, [total, payments, document]);

  const validChangeRemaining = useMemo(() => {
    const result =
      document?.total !== undefined
        ? document?.total -
          payments
            .filter((p) => p.status === PaymentStatusEnum.APPROVE)
            .reduce((acc, payment) => acc + payment.amount, 0)
        : +(
            total -
            payments
              .filter(
                (p) =>
                  p.status !== PaymentStatusEnum.CANCELED &&
                  p.status !== PaymentStatusEnum.REJECT,
              )
              .reduce((acc, payment) => acc + payment.amount, 0)
          ).toFixed(2);
    return result;
  }, [document?.total, payments, total]);

  const paymentMode = useMemo(() => {
    return shipments[0]?.paymentMode;
  }, [shipments]);

  const allProofOfPayments = useMemo(() => {
    let allPOP = true;
    payments.forEach((payment) => {
      if (
        [
          PaymentMethodEnum.ZELLE,
          PaymentMethodEnum.PAGO_MOVIL_P2C,
          PaymentMethodEnum.TRANSFERENCIA_BANCARIA_INMEDIATA,
          PaymentMethodEnum.PAGO_MERCHANT_TARJETA_CONCILIACION_MANUAL,
        ].includes(payment.paymentMethod.paymentMethodID) &&
        payment.status === PaymentStatusEnum.PENDING &&
        !payment.proofOfPayment
      ) {
        allPOP = false;
      }
    });

    return allPOP;
  }, [payments]);

  const isEditMode = useMemo(() => {
    return drafts.some((s) => !!s.shipmentNumber);
  }, [drafts]);

  const onSave = async (): Promise<Undefinable<DocumentInterface>> => {
    // If there are a document, don't save it again
    if (!!document?.documentID) {
      return document;
    }

    const status = total < 0.01 ? DocumentStatus.PAID : DocumentStatus.PENDING;

    const international = !!shipments[0].isInternational;

    // Save document
    const documentData: DocumentInterface = {
      documentID: "",
      accountOwner: owner,
      taxes,
      paymentMode: paymentMode!,
      subTotal,
      total,
      status,
      shipments: shipments,
      payments: [],
      printed: false,
      hasRetention: false, // document.payments.some((p) => p.isRetention)
      igtfAmount: 0,
      balanceAmount: total,
      documentType: DocumentType.ORDER,
      isInternational: international,
    };

    if (
      paymentMode === PaymentMode.COD ||
      paymentMode === PaymentMode.CREDIT ||
      paymentMode === PaymentMode.BOXOFFICE_CREDIT ||
      remaining < 0.01
    ) {
      // Update shipments
      const updatedShipments: ShipmentInterface[] = [];

      for (const shipment of shipments) {
        const response = await updateShipmentStatus(
          ShipmentStatus.ACTIVE,
          shipment.id,
          shipment.shipmentNumber,
          user ?? undefined,
        );

        if (!response || response.didError || !response.model) {
          alertService.error(
            `Error al actualizar la guía`,
            response?.errorMessage,
            {
              autoClose: false,
            },
          );
          return;
        } else {
          updatedShipments.push({
            ...shipment,
            status: ShipmentStatus.ACTIVE,
          });
        }
      }

      documentData.shipments = updatedShipments;

      if (
        paymentMode === PaymentMode.COD ||
        paymentMode === PaymentMode.CREDIT
      ) {
        return documentData;
      }
    }
    const response = await saveDocument(
      documentData,
      exchanges,
      userBU?.code,
      user ?? undefined,
      owner,
      coupon?.couponType.couponDescription,
      applicationID,
      bsExchange,
    );

    if (documentData.isInternational && response.model) {
      response.model.id_solicitud = response.model.documentID;
    }
    if (response.didError || !response.model?.id_solicitud) {
      alertService.error(
        `Error al guardar el documento`,
        response.errorMessage,
        {
          autoClose: false,
        },
      );
      loaderService.stop();
      return;
    }

    const newDocument = {
      ...documentData,
      documentID: response.model.id_solicitud,
    };

    const documentType =
      status === DocumentStatus.PAID ||
      paymentMode === PaymentMode.BOXOFFICE_CREDIT
        ? DocumentType.INVOICE
        : DocumentType.ORDER;

    if (documentType === DocumentType.INVOICE) {
      const response = await updateDocumentStatus(
        newDocument.documentID,
        status,
        documentType,
        user ?? undefined,
        status === DocumentStatus.PAID ? userBU?.code : undefined, // set BU invoicer,
        DocumentType.ORDER,
      );

      if (!!response.didError) {
        alertService.error(
          "Hubo un error actualizando el estado de la orden",
          response.errorMessage,
          {
            autoClose: false,
          },
        );
      } else {
        newDocument.status = status;
        newDocument.documentType = documentType;
      }
    }

    loaderService.stop();

    return await updateDocument(newDocument);
  };

  const updateDocument = async (document?: DocumentInterface) => {
    if (!document?.documentID) {
      return;
    }

    loaderService.start();

    const documentResponse = await getDocument(document.documentID);

    if (!documentResponse) {
      loaderService.stop();
      return undefined;
    }

    let updatedDocument = {
      ...document!,
      documentNumber: documentResponse.documentNumber,
      payments: documentResponse.payments,
      total: documentResponse.total,
      status: documentResponse.status,
      balanceAmount: documentResponse.balanceAmount,
      baseBalanceAmount: documentResponse.baseBalanceAmount,
      igtfAmount: documentResponse.igtfAmount,
      taxes: documentResponse.taxes,
    };

    loaderService.stop();
    dispatch(setShipmentCreateDocument(updatedDocument));

    return updatedDocument;
  };

  const onPay = async (
    payment: PaymentInterface,
    avoidVerifications?: boolean,
  ) => {
    const paymentVerified =
      avoidVerifications || user?.roleName === "Superadministrador";
    let result = {
      error: false,
      message: "",
    };

    loaderService.start();
    let savedDocument;

    if (document?.documentID) {
      savedDocument = document;
    } else {
      savedDocument = await onSave();
      if (!savedDocument) {
        loaderService.stop();
        return {
          error: true,
          message: "Error al crear el documento",
        };
      }
    }

    if (
      paymentVerified &&
      payment.paymentMethod.paymentMethodID !== PaymentMethodEnum.MERCHANT
    ) {
      payment.paymentMethod = {
        ...payment.paymentMethod,
        paymentMethodID: Math.abs(payment.paymentMethod.paymentMethodID),
      };
    } else if (
      paymentVerified &&
      payment.paymentMethod.paymentMethodID === PaymentMethodEnum.MERCHANT
    ) {
      payment.paymentMethod = {
        ...payment.paymentMethod,
        paymentMethodID: PaymentMethodEnum.PAGO_MERCHANT_TARJETA_DE_DEBITO,
      };
    }

    // Merchant payment
    else if (
      payment.paymentMethod.paymentMethodID === PaymentMethodEnum.MERCHANT ||
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MERCHANT_P2C ||
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MERCHANT_TARJETAS_DE_CREDITO ||
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MERCHANT_TARJETA_DE_DEBITO ||
      payment.paymentMethod.paymentMethodID === PaymentMethodEnum.ZELLE
    ) {
      let ci = payment.clientIdentifier!;
      let amount = currencyExchange(payment.amount, exchanges, "BS");
      let zelleDescription;

      if (payment.paymentMethod.paymentMethodID === PaymentMethodEnum.ZELLE) {
        zelleDescription = payment.clientIdentifier!;
        amount = currencyExchange(payment.amount, exchanges, "USD");
        payment.destBankID = 32;
      } else {
        payment.paymentMethod.bankID = 27;
        payment.paymentMethod.currencyID = 1;
        payment.paymentMethod.paymentMethodID =
          PaymentMethodEnum.PAGO_MERCHANT_TARJETA_DE_DEBITO;
      }

      const res = await payMerchant(amount, ci, zelleDescription);

      if (!res || res.codRespuesta !== "00") {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,
          message: res?.mensajeRespuesta ?? "No hubo respuesta de Merchant",
        };
      } else if (res.tipoProducto === "P2C") {
        payment.paymentMethod.paymentMethodID =
          PaymentMethodEnum.PAGO_MERCHANT_P2C;
        payment.paymentMethod.bankID = 4;
        payment.reference = res?.numeroReferencia ?? "";
      } else if (res.tipoProducto === "ZELLE") {
        payment.reference = `${res?.numeroReferencia ?? ""}:${
          res?.numSeq ?? ""
        }:${res?.terminalVirtual ?? ""}`;
      } else {
        payment.reference = `${res?.numSeq ?? ""}:${
          res?.terminalVirtual ?? ""
        }:${res?.numeroAutorizacion}`;
        payment.status = PaymentStatusEnum.PENDING;
      }

      if (
        res?.codRespuesta === "00" &&
        (res?.numSeq === 0 ||
          res?.terminalVirtual === "" ||
          res?.numeroAutorizacion === "")
      ) {
        const request = JSON.stringify({
          amount,
          clientIdentifier: ci,
          zelleDescription,
        });
        await merchantLogService({
          request: request,
          response: JSON.stringify(res),
          endpoint: "Merchant payment",
          responseMessage: res?.mensajeRespuesta,
        });
      }
    }

    // P2C payment - Bancaribe
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_P2C &&
      payment.destBankID === 6
    ) {
      const res = await consultBC(
        payment.clientIdentifier!,
        currencyExchange(payment.amount, exchanges, "BS"),
        payment.phone!,
        payment.reference!,
        "PM",
        "",
      );

      if (!res?.success) {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,

          message: !!res?.message
            ? res.message.code + " " + res.message.message
            : "Error al procesar el pago",
        };
      } else {
        payment.reference = res?.codigoConfirmacion;
      }
    }

    // P2C payment - Mercantil
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_P2C &&
      payment.destBankID === 4
    ) {
      const res = await mercantilMobilePaymentSearch(
        payment.phone!,
        moment(payment.paymentDate).toDate(),
        payment.reference!,
        currencyExchange(payment.amount, exchanges, "BS"),
        payment.attempts ?? 1,
      );

      if (res?.didError || res.model?.transaction_list?.length === 0) {
        payment.status = PaymentStatusEnum.REJECT;
        let errorMessage = "";
        if (res.model?.error_list && res.model?.error_list.length > 0) {
          errorMessage = res.model?.error_list
            .map((error) => error.description)
            .join(", ");
        }
        result = {
          error: true,
          message: errorMessage || "Error al procesar el pago",
        };
      } else if (
        res.model?.transaction_list &&
        res.model?.transaction_list?.length > 0
      ) {
        payment.reference = res?.model?.transaction_list[0].payment_reference;
      }
    }

    // TRF payment - Bancaribe
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.TRANSFERENCIA_BANCARIA_INMEDIATA &&
      payment.destBankID === 6
    ) {
      const res = await consultBC(
        payment.clientIdentifier!,
        currencyExchange(payment.amount, exchanges, "BS"),
        "",
        payment.reference!,
        "TRF",
        "",
      );

      if (!res?.success) {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,

          message: !!res?.message
            ? res.message.code + " " + res.message.message
            : "Error al procesar el pago",
        };
      } else {
        payment.reference = res?.codigoConfirmacion;
      }
    }

    // TRF payment - Mercantil
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.TRANSFERENCIA_BANCARIA_INMEDIATA &&
      payment.destBankID === 4
    ) {
      const res = await mercantilTransferSearch(
        payment.clientIdentifier!,
        moment(payment.paymentDate).toDate(),
        payment.bank!,
        payment.reference!,
        currencyExchange(payment.amount, exchanges, "BS"),
        payment.attempts ?? 1,
      );

      if (res?.didError) {
        payment.status = PaymentStatusEnum.REJECT;
        let errorMessage = "";
        if (res.model?.errorList && res.model?.errorList.length > 0) {
          errorMessage = res.model?.errorList
            .map((error) => error.description)
            .join(", ");
        }
        result = {
          error: true,
          message: errorMessage || "Error al procesar el pago",
        };
      }
      if (
        res.model?.transferSearchList &&
        res.model?.transferSearchList?.length > 0
      ) {
        if (res.model?.transferSearchList[0].trxStatus === "R") {
          payment.status = PaymentStatusEnum.REJECT;
          result = {
            error: true,
            message: "La transferencia fue rechazada por el banco",
          };
        } else if (
          res.model?.transferSearchList[0].previouslySearched === "S"
        ) {
          payment.status = PaymentStatusEnum.REJECT;
          result = {
            error: true,
            message: "El pago ya fue agregado previamente",
          };
        }
      }
    }

    // C2P payment - Bancaribe
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_C2P &&
      payment.destBankID === 6
    ) {
      const res = await sendC2PBC(
        payment.bank!,
        payment.clientIdentifier!,
        currencyExchange(payment.amount, exchanges, "BS"),
        payment.phone!,
        payment.otp,
      );

      if (res?.codigoError !== 0) {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,
          message: res?.descripcionError ?? "Error al procesar el pago",
        };
      }
      payment.reference = res?.secuencial.toString();
    }

    // C2P payment - Mercantil
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_C2P &&
      payment.destBankID === 4
    ) {
      const res = await mercantilC2P(
        payment.phone!,
        payment.clientIdentifier!,
        payment.otp!,
        savedDocument?.documentID!.split("-")[0]!,
        payment.amount,
        payment.bank!,
      );
      if (res?.didError) {
        payment.status = PaymentStatusEnum.REJECT;
        let errorMessage = "";
        if (res.model?.error_list && res.model?.error_list.length > 0) {
          errorMessage = res.model?.error_list
            .map((error) => error.description)
            .join(", ");
        }
        result = {
          error: true,
          message: errorMessage || "Error al procesar el pago",
        };
      } else {
        payment.reference = `${res?.model?.transaction_c2p_response?.payment_reference}:${res?.model?.transaction_c2p_response?.invoice_number}`;
      }
    }

    //Instant debit payment - Banco R4
    else if (
      payment.paymentMethod.paymentMethodID ===
      PaymentMethodEnum.DEBITO_INMEDIATO
    ) {
      const res = await r4InstantDebit(
        payment.bank!,
        payment.amount,
        payment.phone!,
        payment.clientIdentifier!,
        payment.otp!,
        "Cliente Taquilla",
        document
          ? `Pago orden #${document.documentNumber}`
          : "Pago Débito Inmediato",
      );

      if (res?.didError) {
        payment.status = PaymentStatusEnum.REJECT;
        let errorMessage = "";
        if (res.errorMessage) {
          errorMessage = res.errorMessage;
        }
        result = {
          error: true,
          message: errorMessage || "Error al procesar el pago",
        };
      } else {
        payment.destBankID = BankEnum.R4;
        payment.reference = `${res?.model?.reference}`;
      }
    }

    // POS payment
    else if (
      payment.paymentMethod.paymentMethodID === PaymentMethodEnum.PUNTO_DE_VENTA
    ) {
      payment.reference = `${payment.affiliateNumber}:${payment.batchNumber}`;
    }

    // B2P change payment - Bancaribe
    if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO &&
      payment.destBankID === BankEnum.BANCARIBE
    ) {
      const res = await sendB2PBC(
        payment.bank!,
        payment.clientIdentifier!,
        payment.amount,
        payment.phone!,
        document ? `Asociado a orden #${document.documentID}` : "Pago 1 B2P",
        "Factura",
      );

      if (res?.codigoError !== 0) {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,
          message: res?.descripcionError ?? "Error al procesar el pago",
        };
      } else {
        payment.reference = res?.codigoConfirmacion;
      }
    }

    // B2P change payment - Mercantil
    if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO &&
      payment.destBankID === BankEnum.MERCANTIL
    ) {
      const res = await mercantilChange(
        payment.phone!,
        payment.clientIdentifier!,
        document?.documentID!.split("-")[0]!,
        payment.amount,
        payment.bank!,
      );
      if (res?.didError) {
        payment.status = PaymentStatusEnum.REJECT;
        let errorMessage = "";
        if (res.model?.error_list && res.model?.error_list.length > 0) {
          errorMessage = res.model?.error_list
            .map((error) => error.description)
            .join(", ");
        }
        result = {
          error: true,
          message: errorMessage || "Error al procesar el pago",
        };
      } else {
        payment.reference = `${res?.model?.transaction_c2p_response?.payment_reference}:${res?.model?.transaction_c2p_response?.invoice_number}`;
      }
    }

    // Instant credit payment - Banco R4
    if (
      payment.paymentMethod.paymentMethodID ===
      PaymentMethodEnum.CREDITO_INMEDIATO
    ) {
      const res = await r4InstantCredit(
        payment.bank!,
        payment.amount,
        payment.phone!,
        payment.clientIdentifier!,
        document
          ? `Vuelto orden#${document.documentNumber}`
          : "Vuelto Crédito inmediato",
      );

      if (res?.didError) {
        payment.status = PaymentStatusEnum.REJECT;
        let errorMessage = "";
        if (res.errorMessage) {
          errorMessage = res.errorMessage;
        }
        result = {
          error: true,
          message: errorMessage || "Error al procesar el Vuelto",
        };
      } else {
        payment.reference = `${res?.model?.reference}`;
      }
    }

    // Refund requests
    if (
      [
        PaymentMethodEnum.REINTEGRO,
        PaymentMethodEnum.CREDITO_INMEDIATO_CM,
      ].includes(payment.paymentMethod.paymentMethodID)
    ) {
      const res = await ReintegrationRequest(
        user?.userLogin!,
        businessUnit!,
        document!,
        payment,
        document?.accountOwner!,
      );

      if (payment.paymentMethod.paymentMethodID === PaymentMethodEnum.REINTEGRO)
        payment.paymentMethod.bankAccountID = undefined;


      if (res.didError) {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,

          message: res.errorMessage,
        };
      } else {
        payment.status = PaymentStatusEnum.PENDING;
      }
    }

    // Zelle Reintegration
    if (
      payment.paymentMethod.paymentMethodID ===
      PaymentMethodEnum.REINTEGRO_ZELLE
    ) {
      payment.status = PaymentStatusEnum.PENDING;

      const res = await ReintegrationRequest(
        user?.userLogin!,
        businessUnit!,
        document!,
        payment,
        document?.accountOwner!,
      );

      if (res.didError) {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,
          message: res.errorMessage,
        };
      }
    }

    // verifies that the payment was not verified before
    let verified = false;
    if (
      payment.status !== PaymentStatusEnum.REJECT &&
      payment.paymentMethod.paymentMethodID !== PaymentMethodEnum.EFECTIVO &&
      payment.paymentMethod.paymentMethodID !==
        PaymentMethodEnum.REINTEGRO_ZELLE
    )
      document?.payments.forEach((p) => {
        if (
          ((p.reference === payment.reference &&
            p.reference !== "" &&
            payment.paymentMethod.paymentMethodID !==
              PaymentMethodEnum.ZELLE) ||
            (payment.reference === "" && p.amount === payment.amount)) &&
          p.status !== PaymentStatusEnum.REJECT
        ) {
          verified = true;
        }
      });

    if (verified) {
      payment.status = PaymentStatusEnum.REJECT;
      result = {
        error: true,
        message: "El pago ya fue agregado previamente",
      };
    }

    // Convert amount to negative if change
    if (
      [
        PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO,
        PaymentMethodEnum.VUELTO_EFECTIVO,
        PaymentMethodEnum.REINTEGRO,
        PaymentMethodEnum.REINTEGRO_ZELLE,
        PaymentMethodEnum.CREDITO_INMEDIATO,
        PaymentMethodEnum.CREDITO_INMEDIATO_CM,
        PaymentMethodEnum.CREDITO_INMEDIATO_CUENTA,
      ].includes(payment.paymentMethod.paymentMethodID)
    ) {
      payment.amount = -payment.amount;
      payment.paymentAmount = -(payment.paymentAmount ?? 0);
    }

    // Change currency
    let currencyId = payment.paymentMethod.currencyID;

    const response = await savePayment(
      payment,
      savedDocument!,
      userBU?.code,
      user ?? undefined,
      currencyId,
      applicationID
    );
    if (response.didError || !response.model) {
      loaderService.stop();
      alertService.error("Error al guardar el pago", response.errorMessage, {
        autoClose: false,
      });
      return {
        error: true,
        message: "Error al guardar el pago",
      };
    }
    if (payment.status === PaymentStatusEnum.PENDING) {
      alertService.success(
        "El pago se registró exitosamente",
        "Deberá ser conciliado por Casa Matriz",
        { autoCloseDelay: 15000 },
      );
    }

    // Update document
    const updatedDoc = await updateDocument(savedDocument);

    // Guard: if getDocument() returned stale data that omits the newly saved
    // PENDING payment, force it into Redux so allProofOfPayments blocks
    // finalization until the user uploads proof.
    const proofRequiredMethods = [
      PaymentMethodEnum.ZELLE,
      PaymentMethodEnum.PAGO_MOVIL_P2C,
      PaymentMethodEnum.TRANSFERENCIA_BANCARIA_INMEDIATA,
      PaymentMethodEnum.PAGO_MERCHANT_TARJETA_CONCILIACION_MANUAL,
    ];
    if (
      payment.status === PaymentStatusEnum.PENDING &&
      proofRequiredMethods.includes(payment.paymentMethod.paymentMethodID) &&
      updatedDoc &&
      !updatedDoc.payments.some(
        (p) =>
          p.status === PaymentStatusEnum.PENDING &&
          p.paymentMethod.paymentMethodID ===
            payment.paymentMethod.paymentMethodID &&
          p.reference === payment.reference,
      )
    ) {
      dispatch(
        setShipmentCreateDocument({
          ...updatedDoc,
          payments: [...updatedDoc.payments, payment],
        }),
      );
    }

    loaderService.stop();
    dispatch(setShipmentCreatePhase(ShipmentCreatePhase.PAYMENT));

    return result;
  };

  const onCancelRetention = async (retention: PaymentInterface) => {
    loaderService.start();
    const response = await updatePayment({
      documentID: document?.documentID ?? retention.documentID!,
      paymentDetailID: retention.paymentDetailID!,
      statusID: PaymentStatusEnum.CANCELED,
      user: user!,
    });

    if (!!response.didError) {
      alertService.error(
        "Error al cancelar la retención",
        response.errorMessage,
        {
          autoClose: false,
        },
      );
      loaderService.stop();
      return;
    }

    const documentResponse = await getDocument(document!.documentID);
    if (!documentResponse) {
      loaderService.stop();
      return;
    }

    const totalPaid = documentResponse.payments
      .filter(
        (p) =>
          p.status !== PaymentStatusEnum.CANCELED &&
          p.status !== PaymentStatusEnum.REJECT &&
          p.status !== PaymentStatusEnum.PENDING,
      )
      .reduce((acc, payment) => acc + payment.amount, 0);
    const remaining = total - totalPaid;
    const status =
      (documentResponse.balanceAmount ?? remaining) < 0.01
        ? DocumentStatus.PAID
        : DocumentStatus.PARTIAL_PAID;

    let updatedDocument = {
      ...document!,
      payments: documentResponse.payments,
      total: documentResponse.total ?? total,
      status,
      balanceAmount: documentResponse.balanceAmount,
      baseBalanceAmount: documentResponse.baseBalanceAmount,
    };

    // Update document status
    if (document?.status !== status) {
      const response = await updateDocumentStatus(
        updatedDocument.documentID,
        status,
        undefined,
        user ?? undefined,
        status === DocumentStatus.PAID ? userBU?.code : undefined, // set BU invoicer,
        updatedDocument.documentType,
      );
      if (!!response.didError) {
        alertService.error(
          "Hubo un error actualizando el estado de la orden",
          response.errorMessage,
          {
            autoClose: false,
          },
        );
      } else {
        updatedDocument.status = status;
      }
    }

    loaderService.stop();
    dispatch(setShipmentCreateDocument(updatedDocument));
    dispatch(setShipmentCreatePhase(ShipmentCreatePhase.PAYMENT));
  };

  const onCancelPayment = async (transaction: PaymentInterface) => {
    loaderService.start();

    if (VTIDs.length === 0) {
      alertService.error("No se pudo obtener el VTID");
      loaderService.stop();
      return;
    }

    if (transaction.reference) {
      const paymentVtid = transaction.reference.split(":")[1];

      if (VTIDs.includes(paymentVtid)) {
        const numSeq = transaction.reference.split(":")[0];
        const res = await annulationMerchant(numSeq);

        if (res !== null && res.codRespuesta === "00") {
          const response = await updatePayment({
            documentID: document?.documentID ?? transaction.documentID!,
            paymentDetailID: transaction.paymentDetailID!,
            statusID: PaymentStatusEnum.CANCELED,
            user: user!,
          });

          if (response.didError) {
            alertService.error(
              "Error al cancelar el pago",
              response.errorMessage,
              {
                autoClose: false,
              },
            );
            loaderService.stop();
            return;
          }
          alertService.success("El pago se anuló exitosamente");

          const documentResponse = await getDocument(document!.documentID);
          if (!documentResponse) {
            alertService.error("Error al obtener la orden");
            loaderService.stop();
            return;
          }

          const totalPaid = documentResponse.payments
            .filter(
              (p) =>
                p.status !== PaymentStatusEnum.CANCELED &&
                p.status !== PaymentStatusEnum.REJECT &&
                p.status !== PaymentStatusEnum.PENDING,
            )
            .reduce((acc, payment) => acc + payment.amount, 0);
          const remaining = total - totalPaid;
          const status =
            (documentResponse.balanceAmount ?? remaining) < 0.01
              ? DocumentStatus.PAID
              : DocumentStatus.PARTIAL_PAID;

          let updatedDocument = {
            ...document!,
            payments: documentResponse.payments,
            total: documentResponse.total ?? total,
            status,
            balanceAmount: documentResponse.balanceAmount,
            baseBalanceAmount: documentResponse.baseBalanceAmount,
          };

          // Update document status
          if (document?.status !== status) {
            const response = await updateDocumentStatus(
              updatedDocument.documentID,
              status,
              undefined,
              user ?? undefined,
              status === DocumentStatus.PAID ? userBU?.code : undefined, // set BU invoicer,
              updatedDocument.documentType,
            );
            if (response.didError) {
              alertService.error(
                "Hubo un error actualizando el estado de la orden",
                response.errorMessage,
                {
                  autoClose: false,
                },
              );
            } else {
              updatedDocument.status = status;
            }
          }

          loaderService.stop();
          dispatch(setShipmentCreateDocument(updatedDocument));
          dispatch(setShipmentCreatePhase(ShipmentCreatePhase.PAYMENT));
          return;
        }
        alertService.error(
          "No se pudo anular el pago",
          !!res ? `Código de error: ${res.codRespuesta}` : undefined,
          {
            autoClose: false,
          },
        );
        loaderService.stop();
        return;
      }
      alertService.error("Este pago no se proceso con el terminal actual");
      loaderService.stop();
      return;
    }
    alertService.error(
      "La operación no tiene terminal asociado, no se puede anular",
    );
    loaderService.stop();
  };

  const onApproveReintegration = async (
    transaction: PaymentInterface,
    bankAccount?: BankAccountInterface,
    transactionDetails?: PaymentDetailsDto,
  ) => {
    loaderService.start();
    let reference = transaction.reference;
    if (
      transaction.paymentMethod.paymentMethodID ===
      PaymentMethodEnum.CREDITO_INMEDIATO_CM
    ) {
      const res = await r4InstantCredit(
        transactionDetails?.destinationBankCode!,
        -transactionDetails?.amount!,
        transactionDetails?.phoneNumber!,
        `${transactionDetails?.identificationType?.slice(0, -1)}${
          transactionDetails?.identificationNumber
        }`,
        document
          ? `Vuelto orden #${document.documentNumber}`
          : "Vuelto Crédito inmediato",
      );

      if (res?.didError) {
        alertService.error(
          res.errorMessage || "Error al procesar el Crédito Inmediato",
        );
        loaderService.stop();
        return;
      }

      reference = `${res?.model?.reference}`;
    }

    if (
      transaction.paymentMethod.paymentMethodID ===
      PaymentMethodEnum.REINTEGRO_NO_PROCESADO
    ) {
      await onCancelRetention(transaction);

      const newPayment: PaymentInterface = {
        ...transaction,
        amount: transaction.amount,
        paymentAmount: transaction.amount,
        paymentDetailID: undefined,
        status: PaymentStatusEnum.APPROVE,
        destBankID: undefined,
        paymentMethod: {
          ...transaction.paymentMethod,
          paymentMethodID: PaymentMethodEnum.REINTEGRO_NO_PROCESADO,
        },
      };

      const response = await savePayment(
        newPayment,
        document!,
        userBU?.code,
        user ?? undefined,
        transaction.paymentMethod.currencyID
      );
      if (response.didError || !response.model) {
        alertService.error(response.message);
        loaderService.stop();
        return;
      }
    } else {
      const response = await updatePayment({
        documentID: document!.documentID,
        paymentDetailID: transaction.paymentDetailID!,
        bankAccountID: bankAccount?.bankAccountID,
        referenceNum: reference,
        sourceBankID: bankAccount?.bankID,
        statusID: PaymentStatusEnum.APPROVE,
        user: user!,
      });

      if (response.didError) {
        alertService.error("No se pudo aprobar el reintegro");
        loaderService.stop();
        return;
      }
    }
    alertService.success("Reintegro aprobado con éxito");

    const documentResponse = await getDocument(document!.documentID);
    if (!documentResponse) {
      alertService.error("Error al obtener la orden");
      loaderService.stop();
      return;
    }

    const totalPaid = documentResponse.payments
      .filter(
        (p) =>
          p.status !== PaymentStatusEnum.CANCELED &&
          p.status !== PaymentStatusEnum.REJECT,
      )
      .reduce((acc, payment) => acc + payment.amount, 0);
    const remaining = total - totalPaid;
    const status =
      (documentResponse.balanceAmount ?? remaining) < 0.01
        ? DocumentStatus.PAID
        : DocumentStatus.PARTIAL_PAID;

    let updatedDocument = {
      ...document!,
      payments: documentResponse.payments,
      total: documentResponse.total ?? total,
      status,
      balanceAmount: documentResponse.balanceAmount,
      baseBalanceAmount: documentResponse.baseBalanceAmount,
    };

    // Update document status
    if (document?.status !== status) {
      const response = await updateDocumentStatus(
        updatedDocument.documentID,
        status,
        undefined,
        user ?? undefined,
        status === DocumentStatus.PAID ? userBU?.code : undefined, // set BU invoicer,
        updatedDocument.documentType,
      );
      if (response.didError) {
        alertService.error(
          "Hubo un error actualizando el estado de la orden",
          response.errorMessage,
          {
            autoClose: false,
          },
        );
      } else {
        updatedDocument.status = status;
      }
    }

    loaderService.stop();
    dispatch(setShipmentCreateDocument(updatedDocument));
    dispatch(setShipmentCreatePhase(ShipmentCreatePhase.PAYMENT));
  };

  const onSaveProofOfPayment = async (
    file: File,
    retention: PaymentInterface,
    user: UserInterface,
    statusID: number,
    reference?: string,
    isModification?: boolean,
  ) => {
    loaderService.start();
    const saved = await saveProofOfPayment(
      file,
      retention,
      user,
      statusID,
      reference,
      isModification,
      document?.documentID,
    );

    if (saved.didError || !saved.model) {
      loaderService.stop();
      alertService.error(
        "Error al guardar el comprobante de pago",
        saved.errorMessage,
        {
          autoClose: false,
        },
      );
      return;
    }
    alertService.success("El comprobante se guardó exitosamente");

    const newPayments = payments.map((payment) => {
      if (payment !== retention) return payment;

      return {
        ...payment,
        proofOfPayment: saved.model!,
      };
    });

    const updatedDocument: DocumentInterface = {
      ...document!,
      payments: newPayments,
    };
    // const response = await saveDocument(
    //   updatedDocument,
    //   userBU?.code,
    //   user ?? undefined
    // );
    // if (!!response.didError) {
    //   alertService.error("Error al guardar la orden");
    //   return;
    // }

    loaderService.stop();
    dispatch(
      setShipmentCreateDocument({
        ...updatedDocument,
        //documentID: response.model!.id_solicitud,
      }),
    );
  };

  const onDeleteProofOfPayment = async (retention: PaymentInterface) => {
    loaderService.start();

    const response = await deleteFileDO(retention.proofOfPayment!);

    if (response.didError || !response.model) {
      loaderService.stop();
      alertService.error(
        "Error al eliminar el comprobante de pago",
        response.errorMessage,
        {
          autoClose: false,
        },
      );
      return;
    }

    alertService.success("El comprobante se eliminó exitosamente");

    const newPayments = payments.map((payment) => {
      if (payment !== retention) return payment;

      return {
        ...payment,
        proofOfPayment: undefined,
      };
    });

    const update = await updatePayment({
      documentID: document?.documentID ?? retention.documentID!,
      paymentDetailID: retention.paymentDetailID!,
      statusID: PaymentStatusEnum.PENDING,
      user: user!,
    });

    if (update.didError) {
      alertService.error(
        "Error al eliminar el comprobante",
        update.errorMessage,
        {
          autoClose: false,
        },
      );
      loaderService.stop();
      return;
    }

    const updatedDocument: DocumentInterface = {
      ...document!,
      payments: newPayments,
    };

    loaderService.stop();
    dispatch(
      setShipmentCreateDocument({
        ...updatedDocument,
      }),
    );
  };

  const onFinish = async () => {
    const documentExists = !!document?.documentID;
    const needPay = paymentMode === PaymentMode.CONTADO && remaining >= 0.01;

    if (!needPay && !liabilityWaiverExist) {
      dispatch(setShipmentCreatePhase(ShipmentCreatePhase.RESUME));
    } else if (liabilityWaiverExist && documentExists) {
      dispatch(clearShipmentCreate());
      navigate(`/documents/${document.documentID}`);
    } else if (liabilityWaiverExist && !documentExists) {
      dispatch(clearShipmentCreate());
      navigate(`/shipments/${shipments[0].shipmentNumber}`);
    } else {
      dispatch(clearShipmentCreate());
    }
  };

  const createShipments = async (
    shipments: ShipmentInterface[],
    triggerCouponAnimation: boolean = true
  ) => {
    const newShipments: ShipmentInterface[] = shipments.map((shipment) => {
      return {
        ...shipment!,
        status: ShipmentStatus.DRAFT,
      };
    });

    const savedShipments: ShipmentInterface[] = [];
    let index = 0;
    for (const shipment of newShipments) {
      if (!!shipment.id) {
        savedShipments.push(shipment);
        index++;
        continue;
      }

      const response = await saveShipment(
        shipment,
        user ?? undefined,
        applicationID,
        invoices[index] ?? undefined,
      );

      if (!response || response.didError || !response.model) {
        alertService.error(`Error al guardar la guía`, response?.errorMessage, {
          autoClose: false,
        });
        return;
      }

      savedShipments.push({
        ...shipment,
        id: response.model.shipmentHeaderID,
        shipmentNumber: response.model.shipmentNumber.toString(),
      });
    }

    // Win-promo animation: only fires when (a) the caller signaled that the
    // VTE coupon line was confirmed unchanged by the pre-save re-check, and
    // (b) the saved shipment actually carries a VTE coupon item of magnitude
    // 50 or 100. Backend stores discount as a negative value (e.g. -50 ==
    // 50% off) so we compare on the absolute magnitude.
    if (triggerCouponAnimation) {
      const vteItem = savedShipments[0]?.items.find(
        isVuelveTuEnvioPremiadoItem
      );
      const couponMagnitude = Math.abs(vteItem?.couponValue ?? 0);
      if (couponMagnitude === 100) {
        setRedeemedCouponValue(100);
      } else if (couponMagnitude === 50) {
        setRedeemedCouponValue(50);
      }
    }

    return savedShipments;
  };

  const updateShipments = async (shipments: ShipmentInterface[]) => {
    const updatedShipments: ShipmentInterface[] = [];

    let index = 0;
    for (const shipment of shipments) {
      if (!!shipment.id) {
        const response = await updateShipment(
          shipment,
          true,
          true,
          user ?? undefined,
          applicationID,
          invoices[index] ?? undefined,
        );

        if (!response || response.didError || !response.model) {
          alertService.error(
            `Error al actualizar la guía`,
            response?.errorMessage,
            {
              autoClose: false,
            },
          );
          return;
        } else {
          updatedShipments.push({
            ...shipment,
          });
        }
      } else {
        const response = await saveShipment(
          shipment,
          user ?? undefined,
          applicationID,
          invoices[index] ?? undefined,
        );

        if (!response || response.didError || !response.model) {
          alertService.error(
            `Error al guardar la guía`,
            response?.errorMessage,
            {
              autoClose: false,
            },
          );
          return;
        } else {
          updatedShipments.push({
            ...shipment,
            id: response.model.shipmentHeaderID,
            shipmentNumber: response.model.shipmentNumber.toString(),
          });
        }
      }

      index++;
    }

    return updatedShipments;
  };

  /**
   * Pre-save check for the "Vuelve Tu Envío Premiado" coupon. Runs only for
   * drafts that already carry a VTE coupon line item. Does NOT substitute
   * the draft's items — it just re-pulls rates from the backend and compares
   * the VTE items returned vs the ones in the draft.
   *
   * Returns true if every draft with a VTE item still gets the same VTE
   * coupon back (same couponID + same magnitude). Returns false if any draft
   * lost or changed its VTE coupon between the form's last reactive update
   * and this moment (e.g. another cashier claimed it). Network errors are
   * treated as "no observed change" — fail open so a flaky connection
   * doesn't suppress a legitimate win animation.
   */
  const verifyVteCouponPersistence = async (
    candidates: ShipmentInterface[]
  ): Promise<boolean> => {
    const draftsWithVte = candidates.filter((draft) =>
      (draft.items ?? []).some(isVuelveTuEnvioPremiadoItem)
    );
    if (draftsWithVte.length === 0) return false;

    for (const draft of draftsWithVte) {
      try {
        const response = await getShipmentRates(
          draft.service ?? ShipmentService.STANDARD,
          draft.paymentMode ?? PaymentMode.COD,
          draft.deliveryType ?? DeliveryType.AT_OFFICE,
          draft.buSource?.code ?? "",
          draft.buConsignee?.code ?? "",
          draft.shipper?.id ?? "",
          draft.consignee?.id ?? "",
          draft.accountBillTo?.id,
          draft.accountSiteID ?? undefined,
          draft.shipperAddress ?? draft.buSource?.location,
          draft.consigneeAddress,
          draft.items ?? [],
          draft.pieces ?? [],
          (draft.pieces ?? []).reduce((acc, p) => acc + p.value, 0) > 0,
          new Date().toISOString(),
          applicationID,
          draft.id
        );

        if (response.didError || !response.model) {
          // Treat as inconclusive — don't penalize the draft.
          continue;
        }

        const draftVte = (draft.items ?? []).filter(
          isVuelveTuEnvioPremiadoItem
        );
        const responseVte = response.model.items.filter(
          isVuelveTuEnvioPremiadoItem
        );

        if (draftVte.length !== responseVte.length) return false;

        const allMatch = draftVte.every((draftItem) =>
          responseVte.some(
            (responseItem) =>
              responseItem.couponID === draftItem.couponID &&
              Math.abs(responseItem.couponValue ?? 0) ===
                Math.abs(draftItem.couponValue ?? 0)
          )
        );
        if (!allMatch) return false;
      } catch {
        // Same fail-open behavior as the didError branch.
        continue;
      }
    }

    return true;
  };

  /**
   * Post-save reconciliation of coupon/promotion lines. Saving a guide is the
   * moment the backend (re)claims and re-evaluates its promotion, so the items
   * the drafts carry client-side (the last reactive quote) may hold a coupon
   * that no longer applies — or miss one just claimed. Re-pull rates per saved
   * guide and adopt the response items as the authoritative quote, so both the
   * Order screen and the drafts rendered after Regresar show the reconciled
   * promotion without waiting for a remount recompute.
   *
   * quoteSignature is intentionally preserved: it fingerprints rate inputs and
   * non-coupon optional item ids only, and both survive reconciliation
   * unchanged, so the post-Regresar short-circuits keep working and display
   * the reconciled quote with zero extra fetches.
   *
   * Fail-open: on any error the shipment keeps its current items (the backend
   * still reconciles the actual discount server-side).
   */
  const reconcileShipmentPromotions = async (
    shipments: ShipmentInterface[]
  ): Promise<ShipmentInterface[]> => {
    return await Promise.all(
      shipments.map(async (shipment) => {
        // International guides price through DHL adjustments applied by the
        // form (applyInternationalRate) and carry no lottery promotion — keep
        // their items untouched.
        if (shipment.deliveryType === DeliveryType.INTERNATIONAL) {
          return shipment;
        }

        try {
          // Items go in VERBATIM (same shape verifyVteCouponPersistence uses):
          // promo lines flow through SearchItemList into the quote's items and
          // the rates response often echoes them without re-attaching coupon
          // metadata, so stripping them from the input would drop a promotion
          // the backend has no reason to re-inject. The mapping's fallback
          // preserves the echoed metadata; newly claimed promos still arrive
          // as extra response items.
          const inputItems = shipment.items ?? [];
          const response = await getShipmentRates(
            shipment.service ?? ShipmentService.STANDARD,
            shipment.paymentMode ?? PaymentMode.COD,
            shipment.deliveryType ?? DeliveryType.AT_OFFICE,
            shipment.buSource?.code ?? "",
            shipment.buConsignee?.code ?? "",
            shipment.shipper?.id ?? "",
            shipment.consignee?.id ?? "",
            shipment.accountBillTo?.id,
            shipment.accountSiteID ?? undefined,
            shipment.shipperAddress ?? shipment.buSource?.location,
            shipment.consigneeAddress,
            inputItems,
            shipment.pieces ?? [],
            (shipment.pieces ?? []).reduce((acc, p) => acc + p.value, 0) > 0,
            new Date().toISOString(),
            applicationID,
            shipment.id
          );
          if (response.didError || !response.model) {
            return shipment;
          }

          const items = response.model.items
            .filter(
              (item) =>
                item.mandatory ||
                !!item.couponID ||
                inputItems.some((s) => s.id === item.id)
            )
            .sort((a, b) => a.order - b.order);
          // Same sanity checks as the form's reactive rate effect: a response
          // without a base item, or with a ZERO-rated base item outside
          // credit mode, is not a usable quote. Adopting a zero-rated quote
          // here would put a 0.00 total on the Order screen and let the
          // guide be activated without charge, with no recompute path (the
          // preserved signature short-circuits the next Regresar).
          if (
            !items.some((item) => item.isItemBase) ||
            (items
              .filter((item) => item.isItemBase)
              .some((item) => !item.rate.value) &&
              shipment.paymentMode !== PaymentMode.CREDIT)
          ) {
            return shipment;
          }

          const absoluteTotal = items
            .filter((item) => !item.rate.isPercentage)
            .reduce((total, item) => total + item.rate.value, 0);
          const percentageTotal = items
            .filter((item) => item.rate.isPercentage)
            .reduce((total, item) => total + item.rate.value, 0);

          return {
            ...shipment,
            items,
            total: absoluteTotal * (1 + percentageTotal / 100),
          };
        } catch {
          return shipment;
        }
      })
    );
  };

  const handleShipmentDraftCreation = async () => {
    loaderService.start();

    const shipments = drafts as ShipmentInterface[];

    // The pre-save re-check runs only for drafts with a VTE coupon line.
    // It does NOT substitute the draft's items — it only decides whether
    // the win-promo animation fires. If the coupon was claimed elsewhere
    // between the last reactive rate update and this click, the animation
    // is suppressed but the draft is saved unchanged (backend will validate
    // and reconcile the actual discount at save time).
    const vteCouponPersists = await verifyVteCouponPersistence(shipments);

    const savedShipments = shipments[0].id
      ? await updateShipments(shipments)
      : await createShipments(shipments, vteCouponPersists);

    if (!savedShipments) {
      loaderService.stop();
      return;
    }

    // The save is the moment the backend (re)claims the promotion: adopt its
    // re-evaluated coupon lines so the Order screen and the drafts shown
    // after Regresar reflect the real quote instead of the pre-save one.
    const reconciledShipments = await reconcileShipmentPromotions(
      savedShipments
    );

    window.scrollTo({ top: 0 });
    dispatch(setShipmentCreateList(reconciledShipments));
    // The guide now holds a (re)claimed promotion on the backend: arm the
    // release so the next rate-relevant edit after Regresar frees it once.
    dispatch(
      setShipmentCreateDrafts(
        reconciledShipments.map((shipment) => ({
          ...shipment,
          promotionReleased: false,
        }))
      )
    );
    dispatch(setShipmentCreateOwner(reconciledShipments[0].accountBillTo));
    dispatch(setShipmentCreatePhase(ShipmentCreatePhase.DOCUMENT));

    loaderService.stop();
  };

  const handleOwnerChange = async (owner: AccountInterface) => {
    if (document) {
      loaderService.start();
      const response = await updateDocumentAccountBillTo(
        document.documentID,
        owner,
        user ?? undefined,
      );
      loaderService.stop();

      if (response.didError) {
        alertService.error(
          "Error al actualizar la cuenta a facturar",
          response.errorMessage,
          {
            autoClose: false,
          },
        );
        return;
      }
    }

    dispatch(setShipmentCreateOwner(owner));
  };

  useEffect(() => {
    const taxes: TaxInterface[] = [
      {
        name: "IVA",
        value: iva,
        type: "fixed",
      },
      {
        name: "Ipostel",
        value: ipostel,
        type: "fixed",
      },
    ];

    if (!!document?.igtfAmount) {
      taxes.push({
        name: "IGTF",
        value: document.igtfAmount,
        type: "fixed",
      });
    }

    dispatch(setShipmentCreateTaxes(taxes));
  }, [ipostel, iva, document, dispatch]);

  useEffect(() => {
    if (shipments[0]) {
      const checkLiabilityWaiver = async () => {
        const response = await searchLiabilityWaiver({
          shipmentHeaderID: shipments[0].id,
        });
        const waiverExists = (response.model?.length ?? 0) > 0;
        setLiabilityWaiverExist(waiverExists);
      };

      checkLiabilityWaiver();
    }
  }, [liabilityWaiverExist, shipments]);

  useEffect(() => {
    updateDocument(document);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== ShipmentCreatePhase.DOCUMENT) {
      return;
    }

    const afunction = async () => {
      const response = await getShipmentBarcodes(
        drafts.map((s) => s.shipmentNumber ?? ""),
      );
      if (response.didError || !response.model) {
        return;
      }
      setShipmentBarcodes(response.model);
    };

    afunction();
  }, [drafts, phase]);

  useEffect(() => {
    if (phase === ShipmentCreatePhase.SHIPMENTS && !isEditMode)
      setOpenShipmentWarningModal(true);
  }, [phase, isEditMode]);

  return (
    <main className="lg:pl-72 pb-32">
      {/* Header */}
      <div className="py-8 sm:px-6 lg:px-8 bg-white relative flex items-center justify-between h-32">
        <header className="ml-4 text-2xl font-bold text-gray-700 ">
          {phase === ShipmentCreatePhase.SHIPMENTS && !isEditMode
            ? "Crear Guía"
            : phase === ShipmentCreatePhase.SHIPMENTS && isEditMode
              ? "Editar Guía"
              : phase === ShipmentCreatePhase.DOCUMENT
                ? "Orden"
                : phase === ShipmentCreatePhase.PAYMENT
                  ? "Pago"
                  : "Resumen"}
        </header>
      </div>

      {/* Body */}
      <div>
        <HorizontalPadding paddingTop>
          {phase === ShipmentCreatePhase.SHIPMENTS && (
            <ShipmentFormList
              onSubmit={handleShipmentDraftCreation}
              invoices={invoices}
              setInvoices={setInvoices}
            />
          )}

          {(phase === ShipmentCreatePhase.DOCUMENT ||
            phase === ShipmentCreatePhase.PAYMENT) && (
            <div className="mt-5 flex flex-1 flex-col gap-6">
              {/* Header */}
              {phase === ShipmentCreatePhase.DOCUMENT && (
                <div className="flex flex-col">
                  <div>
                    <BackButton
                      handleOnRegret={() =>
                        dispatch(
                          setShipmentCreatePhase(ShipmentCreatePhase.SHIPMENTS),
                        )
                      }
                    />
                  </div>
                </div>
              )}

              {/* Show invoice owner.
                  Se muestra en TODAS las modalidades. Antes estaba limitado a
                  CONTADO y BOXOFFICE_CREDIT, así que en CREDIT y COD el operador
                  no podía ver ni corregir al titular — y el arreglo inline de la
                  dirección fiscal vive justo aquí, dentro de ClientDetails. */}
              {(!document || !document.urlDocument) && (
                <ShipmentOwner
                  owner={owner}
                  openCreationModal={setOpenCreationModal}
                  onSelectOwner={handleOwnerChange}
                />
              )}

              {/* Show shipments resumen */}
              <div className={"pr-1"} style={{ flex: 1 }}>
                <ShipmentTable
                  exchanges={exchanges}
                  showDetails
                  fields={[
                    ShipmentField.SHIPPER,
                    ShipmentField.CONSIGNEE,
                    ShipmentField.BU_SOURCE,
                    ShipmentField.DESTINATION,
                    ShipmentField.AMOUNT,
                  ]}
                  shipments={shipments.map((s) => s!)}
                />
              </div>

              {/* Show totals */}
              <PaymentTotal
                exchanges={exchanges}
                taxes={taxes}
                subTotal={subTotal}
                total={document?.total}
                paymentMode={paymentMode}
                baseRemaining={remaining}
                showEstimate={paymentMode === PaymentMode.COD}
              />

              {phase === ShipmentCreatePhase.DOCUMENT &&
                paymentMode !== PaymentMode.CONTADO &&
                (hasFiscalAddress(owner) ||
                  paymentMode === PaymentMode.CREDIT ||
                  paymentMode === PaymentMode.COD) && (
                  <div className="flex flex-col w-full items-center justify-center gap-3 mt-3">
                    <div
                      className={classNames(
                        "py-2 px-4 rounded-lg border bg-white",
                        shipmentBarcodes.length === 0 && "hidden",
                      )}
                    >
                      <p className="text-gray-700">
                        Recuerde imprimir la nueva etiqueta y colocarla sobre la
                        anterior para que la información esté actualizada.
                      </p>
                    </div>

                    <PrimaryButton
                      disabled={loading}
                      className="!px-10 !py-2 !gap-3 items-center"
                      onClick={async () => {
                        loaderService.start();
                        const response = await onSave();
                        loaderService.stop();

                        if (!!response) {
                          onFinish();
                        }
                      }}
                    >
                      <ClipboardDocumentCheckIcon
                        className="h-5 w-5 flex-none text-white-400"
                        aria-hidden="true"
                      />
                      {shipments.some((s) => s.status === ShipmentStatus.ACTIVE)
                        ? "Guardar cambios y finalizar"
                        : "Finalizar y activar guía(s)"}
                    </PrimaryButton>
                  </div>
                )}

              {/* Show pay methods */}
              {(paymentMode === PaymentMode.CONTADO || !!document) &&
                hasFiscalAddress(owner) && (
                  <>
                    <PaymentList
                      editable
                      items={items}
                      payments={payments}
                      remaining={remaining}
                      validChangeRemaining={validChangeRemaining}
                      document={document}
                      owner={owner}
                      exchanges={exchanges}
                      paymentMode={
                        document?.paymentMode ?? shipments[0].paymentMode
                      }
                      onPay={onPay}
                      onCancelRetention={onCancelRetention}
                      onCancelPayment={onCancelPayment}
                      onApproveReintegration={onApproveReintegration}
                      onSaveProofOfPayment={onSaveProofOfPayment}
                      onDeleteProofOfPayment={onDeleteProofOfPayment}
                    />

                    <div className="flex w-full justify-end">
                      {remaining >= 0.01 ? (
                        <SecondaryButton
                          disabled={loading}
                          onClick={() => {
                            if (!allProofOfPayments) setOpenPOPWarningModal(true);
                            else onFinish();
                          }}
                        >
                          Terminar más tarde
                        </SecondaryButton>
                      ) : (
                        <PrimaryButton
                          disabled={loading}
                          onClick={async () => {
                            loaderService.start();
                            if (validChangeRemaining > -0.1) {
                              if (!allProofOfPayments)
                                setOpenPOPWarningModal(true);
                              else {
                                await onSave();
                                onFinish();
                              }
                            } else {
                              setOpenWarningModal(true);
                            }
                            loaderService.stop();
                          }}
                        >
                          <ClipboardDocumentCheckIcon
                            className="h-5 w-5 flex-none text-white-400 mr-2"
                            aria-hidden="true"
                          />
                          Finalizar
                        </PrimaryButton>
                      )}
                    </div>
                  </>
                )}

              {/* The address of the owner of the invoice is required.
                  Usa la lengua de advertencia de la app (Alert.tsx / PieceForm)
                  en vez de text-gray-400, que es el color de "deshabilitado" y
                  además queda en ~2.4:1 de contraste, por debajo del mínimo AA.
                  El arreglo se hace desde el bloque del titular, arriba. */}
              {(paymentMode === PaymentMode.CONTADO || !!document) &&
                !hasFiscalAddress(owner) && (
                  <div className="rounded-lg border bg-yellow-100 border-yellow-400 text-yellow-700 px-8 py-4 text-sm">
                    <p className="font-semibold">
                      {missingFiscalAddressCopy(owner).title}
                    </p>
                    <p className="text-xs">
                      {missingFiscalAddressCopy(owner).body}
                    </p>
                  </div>
                )}
            </div>
          )}

          {phase === ShipmentCreatePhase.RESUME && <ShipmentResume />}
        </HorizontalPadding>

        {/* Proof of Payment Warning Modal */}
        <WarningModal
          openModal={openPOPWarningModal}
          setOpenModal={setOpenPOPWarningModal}
          message="Es necesario agregar todos los comprobantes de los pagos a conciliar para poder continuar."
          onPrimaryButton={() => setOpenPOPWarningModal(false)}
        />

        {/* Creation modal */}
        <ShipmentCreateClientModal
          requireFiscalAddress
          openModal={openCreationModal}
          setOpenModal={setOpenCreationModal}
          // Pasa por `handleOwnerChange` y no por el dispatch directo: si ya hay
          // documento, hay que escribirle el titular nuevo. Despachando a Redux a
          // secas, la pantalla mostraba al cliente recién creado mientras el
          // documento seguía facturando al anterior.
          setSelectedItem={handleOwnerChange}
        />

        {/* Warning modal */}
        <Modal openModal={openWarningModal} setOpenModal={setOpenWarningModal}>
          <div
            className="flex flex-col items-center justify-center"
            style={{ maxWidth: "20rem" }}
          >
            <div className="flex flex-col items-center justify-center w-full">
              <ExclamationTriangleIcon className="w-32 h-32" />
            </div>
            <p className="mt-2 text-lg text-center text-gray-700">
              ¿Está seguro que desea finalizar la orden con un cambio pendiente?
            </p>
            <div className="mt-4 flex flex-row justify-center gap-12">
              <SecondaryButton
                className="px-4"
                onClick={() => setOpenWarningModal(false)}
              >
                Cancelar
              </SecondaryButton>

              <PrimaryButton
                className="px-4"
                disabled={loading}
                onClick={async () => {
                  if (!allProofOfPayments) {
                    setOpenPOPWarningModal(true);
                  } else {
                    setOpenWarningModal(false);
                    await onSave();
                    onFinish();
                  }
                }}
              >
                Aceptar
              </PrimaryButton>
            </div>
          </div>
        </Modal>

        {/* Coupon redeemed animation: modal GIF + confetti + audio with fade in/out */}
        <CouponRedeemedAnimation
          couponValue={redeemedCouponValue}
          onClose={() => setRedeemedCouponValue(null)}
        />

        <WarningModal
          openModal={openShipmentWarningModal}
          setOpenModal={setOpenShipmentWarningModal}
          message={
            <p className="text-center">
              <b>
                Recuérdale al cliente: “En TEALCA, sus envíos hasta los 2 kg
                podrán obtener hasta un 20% de descuento.”
              </b>{" "}
              <br /> ¡Ayúdalo a que su envío pese menos en su bolsillo!
            </p>
          }
          showSecondaryButton={false}
          icon={ModalMegaphoneIcon}
        />
      </div>
    </main>
  );
};

export default ShipmentCreate;
